import { useEffect, useState, useRef } from 'react'
import {
  UserCircleIcon,
  CameraIcon,
  KeyIcon,
  ShieldCheckIcon,
  BellIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  LinkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge, Avatar, InlineSpinner, Button, Modal, ModalBody, ModalFooter } from '@/components/ui'
import { authApi, gamificationApi } from '@/api'
import { useAuthStore } from '@/store/authStore'
import type { UserRole } from '@/types'
import type { Badge as BadgeType, UserLevel } from '@/api/gamification'

export default function ProfilePage() {
  const { user, setUser } = useAuthStore()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'notifications'>('profile')

  // Profile Form
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    job_title: '',
    bio: '',
    linkedin_url: '',
    fiscal_code: '',
    birth_date: '',
    birth_place: '',
  })

  // Password Form
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  })
  const [changingPassword, setChangingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  // 2FA Modal
  const [show2FAModal, setShow2FAModal] = useState(false)
  const [qrCode, setQrCode] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [setting2FA, setSetting2FA] = useState(false)

  // Gamification
  const [gamificationData, setGamificationData] = useState<{
    level: UserLevel
    badges: BadgeType[]
    rank: number
  } | null>(null)

  // Feedback
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  // The user saved at login has only a few fields: load the full profile, so that
  // saving the form does not blank out data that was never shown
  useEffect(() => {
    authApi.getMe()
      .then((fresh) => {
        const current = useAuthStore.getState().user
        setUser({ ...(current || {}), ...fresh } as typeof fresh)
      })
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        job_title: user.job_title || '',
        bio: user.bio || '',
        linkedin_url: user.linkedin_url || '',
        fiscal_code: user.fiscal_code || '',
        birth_date: user.birth_date || '',
        birth_place: user.birth_place || '',
      })
      fetchGamificationData()
      setLoading(false)
    }
  }, [user])

  const fetchGamificationData = async () => {
    try {
      const data = await gamificationApi.getMyStats()
      setGamificationData(data)
    } catch (error) {
      console.error('Error fetching gamification data:', error)
    }
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const result = await authApi.updateAvatar(file)
      if (user) {
        setUser({ ...user, avatar: result.avatar })
      }
      setFeedback({ type: 'success', message: 'Avatar aggiornato con successo' })
    } catch (error) {
      console.error('Error uploading avatar:', error)
      setFeedback({ type: 'error', message: 'Errore nel caricamento dell\'avatar' })
    }
  }

  const handleSaveProfile = async () => {
    setSaving(true)
    setFeedback(null)
    try {
      const { email, ...changes } = profileForm
      const updatedUser = await authApi.updateProfile({ ...changes, birth_date: changes.birth_date || null })
      // The response contains only the profile fields: keep role and permissions
      setUser({ ...user!, ...updatedUser })
      setFeedback({ type: 'success', message: 'Profilo aggiornato con successo' })
    } catch (error: any) {
      console.error('Error saving profile:', error)
      const data = error.response?.data
      const details = data && typeof data === 'object'
        ? Object.values(data).flat().filter((v) => typeof v === 'string').join(' ')
        : ''
      setFeedback({ type: 'error', message: details || 'Errore nel salvataggio del profilo' })
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('Le password non corrispondono')
      return
    }
    if (passwordForm.new_password.length < 8) {
      setPasswordError('La password deve essere di almeno 8 caratteri')
      return
    }

    setChangingPassword(true)
    setPasswordError('')
    setPasswordSuccess(false)

    try {
      await authApi.changePassword({
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password,
        new_password_confirm: passwordForm.confirm_password,
      })
      setPasswordSuccess(true)
      setPasswordForm({ old_password: '', new_password: '', confirm_password: '' })
    } catch (error: any) {
      setPasswordError(error.response?.data?.detail || 'Errore nel cambio password')
    } finally {
      setChangingPassword(false)
    }
  }

  const handleSetup2FA = async () => {
    try {
      const result = await authApi.setup2FA()
      setQrCode(result.qr_code)
      setShow2FAModal(true)
    } catch (error) {
      console.error('Error setting up 2FA:', error)
    }
  }

  const handleVerify2FA = async () => {
    if (!totpCode) return
    setSetting2FA(true)
    try {
      await authApi.verify2FA(totpCode)
      if (user) {
        setUser({ ...user, is_2fa_enabled: true })
      }
      setShow2FAModal(false)
      setTotpCode('')
      setFeedback({ type: 'success', message: 'Autenticazione a due fattori attivata' })
    } catch (error) {
      console.error('Error verifying 2FA:', error)
      setFeedback({ type: 'error', message: 'Codice non valido' })
    } finally {
      setSetting2FA(false)
    }
  }

  const handleDisable2FA = async () => {
    const code = window.prompt('Inserisci il codice 2FA per disattivare')
    if (!code) return
    try {
      await authApi.disable2FA(code)
      if (user) {
        setUser({ ...user, is_2fa_enabled: false })
      }
      setFeedback({ type: 'success', message: 'Autenticazione a due fattori disattivata' })
    } catch (error) {
      console.error('Error disabling 2FA:', error)
      setFeedback({ type: 'error', message: 'Codice non valido' })
    }
  }

  const getRoleBadge = (role: UserRole) => {
    const config: Record<UserRole, { variant: 'default' | 'info' | 'warning' | 'success' | 'danger'; label: string }> = {
      super_admin: { variant: 'danger', label: 'Super Admin' },
      admin: { variant: 'danger', label: 'Admin' },
      scientific_committee: { variant: 'warning', label: 'Comitato Scientifico' },
      expert: { variant: 'info', label: 'Esperto' },
      community_manager: { variant: 'info', label: 'Community Manager' },
      premium_member: { variant: 'success', label: 'Premium' },
      member: { variant: 'default', label: 'Membro' },
      pending: { variant: 'warning', label: 'In attesa' },
      guest: { variant: 'default', label: 'Ospite' },
    }
    return config[role] || { variant: 'default', label: role }
  }

  if (loading || !user) {
    return <InlineSpinner message="Caricamento profilo..." />
  }

  const roleConfig = getRoleBadge(user.role)

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header Card */}
      <Card>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {/* Avatar */}
          <div className="relative">
            <Avatar
              size="xl"
              name={`${user.first_name} ${user.last_name}`}
              src={user.avatar || undefined}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-0 right-0 p-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors"
            >
              <CameraIcon className="h-4 w-4" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </div>

          {/* User Info */}
          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-2xl font-bold text-gray-900">
              {user.first_name} {user.last_name}
            </h1>
            <p className="text-gray-500">{user.email}</p>
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-2">
              <Badge variant={roleConfig.variant}>{roleConfig.label}</Badge>
              {user.is_2fa_enabled && (
                <Badge variant="success">
                  <ShieldCheckIcon className="h-3 w-3 mr-1" />
                  2FA Attivo
                </Badge>
              )}
              {user.is_approved ? (
                <Badge variant="success">
                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                  Verificato
                </Badge>
              ) : (
                <Badge variant="warning">
                  <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                  In attesa
                </Badge>
              )}
            </div>
          </div>

          {/* Gamification Stats */}
          {gamificationData && (
            <div className="flex gap-4 text-center">
              <div className="px-4 py-2 bg-primary-50 rounded-lg">
                <p className="text-2xl font-bold text-primary-600">{gamificationData.level.level}</p>
                <p className="text-xs text-gray-500">Livello</p>
              </div>
              <div className="px-4 py-2 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-700">{gamificationData.level.current_points}</p>
                <p className="text-xs text-gray-500">Punti</p>
              </div>
              <div className="px-4 py-2 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{gamificationData.badges.length}</p>
                <p className="text-xs text-gray-500">Badge</p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Feedback */}
      {feedback && (
        <div className={`p-4 rounded-lg ${feedback.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          <div className="flex items-center gap-2">
            {feedback.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5" />
            ) : (
              <ExclamationCircleIcon className="h-5 w-5" />
            )}
            {feedback.message}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('profile')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'profile'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <UserCircleIcon className="h-5 w-5 inline mr-2" />
            Profilo
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'security'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <KeyIcon className="h-5 w-5 inline mr-2" />
            Sicurezza
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'notifications'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BellIcon className="h-5 w-5 inline mr-2" />
            Notifiche
          </button>
        </nav>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <Card>
          <CardHeader title="Informazioni personali" subtitle="Modifica i tuoi dati personali" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input
                type="text"
                value={profileForm.first_name}
                onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cognome</label>
              <input
                type="text"
                value={profileForm.last_name}
                onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <EnvelopeIcon className="h-4 w-4 inline mr-1" />
                Email
              </label>
              <input
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                disabled
              />
              <p className="text-xs text-gray-400 mt-1">L'email non può essere modificata</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <PhoneIcon className="h-4 w-4 inline mr-1" />
                Telefono
              </label>
              <input
                type="tel"
                value={profileForm.phone}
                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <BuildingOfficeIcon className="h-4 w-4 inline mr-1" />
                Azienda
              </label>
              <input
                type="text"
                value={profileForm.company}
                onChange={(e) => setProfileForm({ ...profileForm, company: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <BriefcaseIcon className="h-4 w-4 inline mr-1" />
                Ruolo professionale
              </label>
              <input
                type="text"
                value={profileForm.job_title}
                onChange={(e) => setProfileForm({ ...profileForm, job_title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="md:col-span-2 pt-4 border-t border-gray-100">
              <h3 className="font-medium text-gray-900">Dati anagrafici</h3>
              <p className="text-sm text-gray-500">
                Obbligatori per ricevere gli attestati dei corsi; sono visibili solo a te e agli amministratori.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Codice fiscale *</label>
              <input
                type="text"
                value={profileForm.fiscal_code}
                onChange={(e) => setProfileForm({ ...profileForm, fiscal_code: e.target.value.toUpperCase() })}
                maxLength={20}
                placeholder="RSSMRA80A01H501U"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono uppercase"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data di nascita *</label>
              <input
                type="date"
                value={profileForm.birth_date}
                onChange={(e) => setProfileForm({ ...profileForm, birth_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Luogo di nascita *</label>
              <input
                type="text"
                value={profileForm.birth_place}
                onChange={(e) => setProfileForm({ ...profileForm, birth_place: e.target.value })}
                placeholder="es. Bari (BA)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <LinkIcon className="h-4 w-4 inline mr-1" />
                LinkedIn
              </label>
              <input
                type="url"
                value={profileForm.linkedin_url}
                onChange={(e) => setProfileForm({ ...profileForm, linkedin_url: e.target.value })}
                placeholder="https://linkedin.com/in/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
              <textarea
                value={profileForm.bio}
                onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                rows={4}
                placeholder="Raccontaci qualcosa di te..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-100 flex justify-end">
            <Button onClick={handleSaveProfile} disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva modifiche'}
            </Button>
          </div>
        </Card>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          {/* Change Password */}
          <Card>
            <CardHeader title="Cambia password" subtitle="Aggiorna la tua password di accesso" />
            <div className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password attuale</label>
                <input
                  type="password"
                  value={passwordForm.old_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nuova password</label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Conferma password</label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {passwordError && (
                <p className="text-sm text-red-600">{passwordError}</p>
              )}
              {passwordSuccess && (
                <p className="text-sm text-green-600">Password cambiata con successo</p>
              )}

              <Button onClick={handleChangePassword} disabled={changingPassword}>
                {changingPassword ? 'Cambio in corso...' : 'Cambia password'}
              </Button>
            </div>
          </Card>

          {/* 2FA */}
          <Card>
            <CardHeader
              title="Autenticazione a due fattori"
              subtitle="Aggiungi un ulteriore livello di sicurezza al tuo account"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${user.is_2fa_enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <DevicePhoneMobileIcon className={`h-6 w-6 ${user.is_2fa_enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Autenticazione TOTP</p>
                  <p className="text-sm text-gray-500">
                    {user.is_2fa_enabled ? 'Attiva' : 'Non configurata'}
                  </p>
                </div>
              </div>
              {user.is_2fa_enabled ? (
                <Button variant="outline" onClick={handleDisable2FA}>
                  Disattiva
                </Button>
              ) : (
                <Button onClick={handleSetup2FA}>
                  Configura
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <Card>
          <CardHeader title="Preferenze notifiche" subtitle="Gestisci come ricevi le notifiche" />
          <div className="space-y-4">
            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Notifiche email</p>
                <p className="text-sm text-gray-500">Ricevi aggiornamenti via email</p>
              </div>
              <input
                type="checkbox"
                defaultChecked
                className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
            </label>
            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Newsletter</p>
                <p className="text-sm text-gray-500">Ricevi la newsletter della community</p>
              </div>
              <input
                type="checkbox"
                checked={user.marketing_consent}
                className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                readOnly
              />
            </label>
            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Nuovi corsi</p>
                <p className="text-sm text-gray-500">Avvisami quando vengono pubblicati nuovi corsi</p>
              </div>
              <input
                type="checkbox"
                defaultChecked
                className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
            </label>
            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Eventi</p>
                <p className="text-sm text-gray-500">Ricevi promemoria per gli eventi</p>
              </div>
              <input
                type="checkbox"
                defaultChecked
                className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
            </label>
            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Forum</p>
                <p className="text-sm text-gray-500">Notifiche per risposte ai tuoi post</p>
              </div>
              <input
                type="checkbox"
                defaultChecked
                className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
            </label>
          </div>
        </Card>
      )}

      {/* 2FA Setup Modal */}
      <Modal isOpen={show2FAModal} onClose={() => setShow2FAModal(false)} title="Configura autenticazione a due fattori">
        <ModalBody>
          <div className="space-y-4 text-center">
            {qrCode && (
              <div className="flex justify-center">
                <img src={qrCode} alt="QR Code" className="w-48 h-48" />
              </div>
            )}
            <p className="text-sm text-gray-600">
              Scansiona il QR code con un'app di autenticazione come Google Authenticator o Authy
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Inserisci il codice</label>
              <input
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                placeholder="000000"
                maxLength={6}
                className="w-32 mx-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-center text-2xl tracking-widest"
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShow2FAModal(false)}>Annulla</Button>
          <Button onClick={handleVerify2FA} disabled={totpCode.length !== 6 || setting2FA}>
            {setting2FA ? 'Verifica...' : 'Verifica e attiva'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
